import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Phone, Globe, Clock, Star, Tag, ChevronLeft, 
  Instagram, Facebook, Twitter, ExternalLink, Mail,
  Calendar, ShoppingBag, Ticket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { KuponCard } from '../components/KuponCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Schedule display helper
const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export const BusinessProfilePage = () => {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    fetchBusiness();
  }, [id]);

  const fetchBusiness = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/directory/businesses/${id}`);
      setBusiness(data);
    } catch (err) {
      console.error('Error fetching business:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-local-sage" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Negocio no encontrado</h2>
          <Link to="/negocios" className="text-local-sage hover:underline">
            Volver al directorio
          </Link>
        </div>
      </div>
    );
  }

  const socialIcons = {
    instagram: Instagram,
    facebook: Facebook,
    twitter: Twitter
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero / Cover */}
      <div className="relative h-64 md:h-80 bg-gradient-to-br from-local-sage/30 to-local-sage/10">
        {business.coverImage && (
          <img 
            src={business.coverImage} 
            alt={business.businessName}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/negocios" 
          className="absolute top-6 left-6 flex items-center gap-2 bg-white/90 dark:bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium hover:bg-white dark:hover:bg-black/70 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Directorio
        </Link>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 -mt-24 relative z-10 pb-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Business Info Card */}
          <div className="lg:col-span-1">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-3xl shadow-xl p-8 border border-border/50"
            >
              {/* Logo & Name */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 rounded-2xl bg-local-sage/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {business.logo ? (
                    <img src={business.logo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl font-bold text-local-sage">
                      {business.businessName?.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold mb-1 truncate">{business.businessName}</h1>
                  {business.businessType && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Tag className="w-4 h-4" />
                      <span>{business.businessType}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rating */}
              {business.averageRating > 0 && (
                <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl mb-6">
                  <div className="flex items-center gap-1">
                    <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    <span className="text-2xl font-bold">{business.averageRating.toFixed(1)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {business.totalReviews} reseñas
                  </div>
                </div>
              )}

              {/* NAP - Name, Address, Phone */}
              <div className="space-y-4 mb-6">
                {(business.address || business.city) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-local-sage flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Dirección</p>
                      <p className="text-muted-foreground text-sm">
                        {business.address && `${business.address}, `}
                        {business.postalCode && `${business.postalCode} `}
                        {business.city}
                      </p>
                    </div>
                  </div>
                )}

                {business.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-local-sage flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Teléfono</p>
                      <a 
                        href={`tel:${business.phone}`} 
                        className="text-local-sage hover:underline text-sm"
                      >
                        {business.phone}
                      </a>
                    </div>
                  </div>
                )}

                {business.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-local-sage flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Email</p>
                      <a 
                        href={`mailto:${business.email}`} 
                        className="text-local-sage hover:underline text-sm"
                      >
                        {business.email}
                      </a>
                    </div>
                  </div>
                )}

                {business.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-local-sage flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Web</p>
                      <a 
                        href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-local-sage hover:underline text-sm flex items-center gap-1"
                      >
                        {business.website.replace(/^https?:\/\//, '')}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Social Links */}
              {business.socialLinks && Object.keys(business.socialLinks).length > 0 && (
                <div className="flex gap-3 pt-4 border-t">
                  {Object.entries(business.socialLinks).map(([platform, url]) => {
                    if (!url) return null;
                    const Icon = socialIcons[platform] || Globe;
                    return (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-local-sage hover:text-white transition-colors"
                      >
                        <Icon className="w-5 h-5" />
                      </a>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column - Tabs Content */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
              {[
                { id: 'info', label: 'Información', icon: Calendar },
                { id: 'kupons', label: `Kupones (${business.activeKuponsCount || 0})`, icon: Ticket },
                { id: 'services', label: 'Servicios', icon: ShoppingBag }
              ].map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? 'default' : 'outline'}
                  onClick={() => setActiveTab(tab.id)}
                  className="rounded-full whitespace-nowrap gap-2"
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </Button>
              ))}
            </div>

            {/* Tab Content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {activeTab === 'info' && (
                <div className="space-y-8">
                  {/* Description */}
                  {business.description && (
                    <div className="bg-card rounded-3xl p-8 border border-border/50">
                      <h2 className="text-xl font-semibold mb-4">Sobre nosotros</h2>
                      <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                        {business.description}
                      </p>
                    </div>
                  )}

                  {/* Schedule */}
                  {business.schedule && (
                    <div className="bg-card rounded-3xl p-8 border border-border/50">
                      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-local-sage" />
                        Horario
                      </h2>
                      <div className="grid gap-3">
                        {DAYS.map((day, i) => {
                          const dayKey = day.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                          const schedule = business.schedule[dayKey];
                          const isClosed = !schedule || schedule === 'cerrado';
                          
                          return (
                            <div 
                              key={day} 
                              className={`flex justify-between items-center py-2 px-4 rounded-xl ${
                                isClosed ? 'bg-muted/50' : 'bg-local-sage/5'
                              }`}
                            >
                              <span className="font-medium">{day}</span>
                              <span className={isClosed ? 'text-muted-foreground' : 'text-local-sage font-medium'}>
                                {isClosed ? 'Cerrado' : schedule}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'kupons' && (
                <div>
                  {!user ? (
                    <div className="bg-card rounded-3xl p-12 border border-border/50 text-center">
                      <Ticket className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Inicia sesión para ver kupones</h3>
                      <p className="text-muted-foreground mb-6">
                        Regístrate o inicia sesión para acceder a las ofertas exclusivas
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Link to="/login">
                          <Button variant="outline" className="rounded-full">
                            Iniciar sesión
                          </Button>
                        </Link>
                        <Link to="/register">
                          <Button className="rounded-full bg-local-sage hover:bg-local-sage/90">
                            Registrarse
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ) : business.activeKupons && business.activeKupons.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-6">
                      {business.activeKupons.map((kupon) => (
                        <KuponCard key={kupon.id} kupon={kupon} />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-card rounded-3xl p-12 border border-border/50 text-center">
                      <Ticket className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">No hay kupones activos</h3>
                      <p className="text-muted-foreground">
                        Este negocio no tiene ofertas disponibles en este momento
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'services' && (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Products */}
                  {business.products && business.products.length > 0 && (
                    <div className="bg-card rounded-3xl p-8 border border-border/50">
                      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5 text-local-sage" />
                        Productos
                      </h2>
                      <div className="space-y-2">
                        {business.products.map((product, i) => (
                          <div key={i} className="flex items-center gap-2 py-2 px-4 bg-muted/50 rounded-xl">
                            <div className="w-2 h-2 rounded-full bg-local-sage" />
                            <span>{product}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Services */}
                  {business.services && business.services.length > 0 && (
                    <div className="bg-card rounded-3xl p-8 border border-border/50">
                      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Tag className="w-5 h-5 text-local-sage" />
                        Servicios
                      </h2>
                      <div className="space-y-2">
                        {business.services.map((service, i) => (
                          <div key={i} className="flex items-center gap-2 py-2 px-4 bg-muted/50 rounded-xl">
                            <div className="w-2 h-2 rounded-full bg-local-sage" />
                            <span>{service}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(!business.products || business.products.length === 0) && 
                   (!business.services || business.services.length === 0) && (
                    <div className="md:col-span-2 bg-card rounded-3xl p-12 border border-border/50 text-center">
                      <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Sin información</h3>
                      <p className="text-muted-foreground">
                        Este negocio aún no ha añadido sus productos o servicios
                      </p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfilePage;
